import { useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Wand2, Download, ChevronDown, ArrowLeft, Sparkles, RefreshCw, Link2, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { generateBriefPDF } from "@/lib/pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXAMPLE = `"I waste 2 hours every Monday manually copying analytics into a client report"
"Wish there was a tool that turns my raw notes into a shareable weekly update"
"Every client asks for a status doc and I dread building it from scratch"
"Notion is too flexible, I just want something that spits out a clean report fast"`;

const LOADING_STEPS = [
  "Reading your snippets",
  "Extracting common pain points",
  "Clustering the themes",
  "Fusing one product concept",
  "Sharpening the value proposition",
];

function LoadingSequence() {
  return (
    <div className="flex flex-col items-start gap-3 font-mono text-sm">
      {LOADING_STEPS.map((step, i) => (
        <motion.div
          key={step}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.55, duration: 0.4 }}
          className="flex items-center gap-3 text-muted-foreground"
        >
          <motion.span
            className="h-1.5 w-1.5 rounded-full bg-emerald-500"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ repeat: Infinity, duration: 1.4, delay: i * 0.55 }}
          />
          {step}
          <span className="text-emerald-500">…</span>
        </motion.div>
      ))}
    </div>
  );
}

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

function Slide({ index, kicker, title, children, testid, isLast }) {
  return (
    <section
      data-testid={testid}
      className="snap-start h-screen w-full flex flex-col justify-center px-8 sm:px-16 md:px-24 lg:px-32 relative z-10"
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        className="max-w-3xl w-full mx-auto"
      >
        <motion.div variants={item} className="mb-6">
          <span className="h-1 w-11 block bg-emerald-500 mb-6" />
          <span className="font-mono text-xs sm:text-sm tracking-widest uppercase text-emerald-500">
            {kicker}
          </span>
        </motion.div>
        <motion.h2
          variants={item}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-8"
        >
          {title}
        </motion.h2>
        {children}
      </motion.div>

      {!isLast && (
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-muted-foreground"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ChevronDown className="h-5 w-5" />
        </motion.div>
      )}
    </section>
  );
}

function SlideFooter({ prompt, section, onReroll, rerolling }) {
  const busy = rerolling === section;
  return (
    <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-6">
      <PromptToggle prompt={prompt} section={section} />
      <button
        data-testid={`reroll-${section}`}
        onClick={() => onReroll(section)}
        disabled={busy}
        className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-emerald-500 transition-colors duration-200 flex items-center gap-2 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        {busy ? "Regenerating…" : "Regenerate"}
      </button>
    </motion.div>
  );
}

function PromptToggle({ prompt, section }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        data-testid={`prompt-toggle-${section}`}
        onClick={() => setOpen((o) => !o)}
        className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-emerald-500 transition-colors duration-200 flex items-center gap-2"
      >
        <Sparkles className="h-3.5 w-3.5" />
        {open ? "Hide prompt" : "Show prompt used"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="font-mono text-xs text-muted-foreground/80 mt-4 leading-relaxed border-l border-border pl-4 overflow-hidden max-w-2xl"
          >
            {prompt}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function IdeaFusion() {
  const [snippets, setSnippets] = useState("");
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [rerolling, setRerolling] = useState(null);
  const [redditUrl, setRedditUrl] = useState("");
  const [fetchingReddit, setFetchingReddit] = useState(false);
  const containerRef = useRef(null);

  const handleFuse = async () => {
    if (snippets.trim().length < 15) {
      toast.error("Paste at least a few real snippets to fuse.");
      return;
    }
    setLoading(true);
    setBrief(null);
    try {
      const { data } = await axios.post(`${API}/fuse`, { snippets });
      setBrief(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fusion failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReddit = async () => {
    if (!redditUrl.trim()) {
      toast.error("Paste a subreddit or Reddit thread URL first.");
      return;
    }
    setFetchingReddit(true);
    try {
      const { data } = await axios.post(`${API}/reddit`, { url: redditUrl });
      setSnippets(data.snippets);
      toast.success(`Pulled ${data.count} posts from Reddit — review and Fuse.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch from Reddit.");
    } finally {
      setFetchingReddit(false);
    }
  };

  const handleReroll = async (section) => {
    setRerolling(section);
    try {
      const { data } = await axios.post(`${API}/fuse/section`, { snippets, section });
      setBrief((b) => ({ ...b, [section]: data }));
      toast.success("Section regenerated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed. Please try again.");
    } finally {
      setRerolling(null);
    }
  };

  const handleReset = () => {
    setBrief(null);
    setSnippets("");
    setRedditUrl("");
  };

  // ---------- RESULT STATE ----------
  if (brief) {
    return (
      <div ref={containerRef} className="snap-container relative">
        <Toaster theme="dark" position="top-center" />

        <button
          data-testid="new-fusion-button"
          onClick={handleReset}
          className="fixed top-6 left-6 z-50 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4" /> New fusion
        </button>

        <button
          data-testid="download-pdf-button"
          onClick={() => generateBriefPDF(brief)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 text-sm font-medium shadow-lg shadow-emerald-500/20 transition-all duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
        >
          <Download className="h-4 w-4" /> Download PDF
        </button>

        <Slide index={1} kicker="01 — Product Concept" title="The Concept" testid="slide-product-concept">
          <motion.p variants={item} data-testid="product-concept-content" className="text-lg sm:text-xl leading-relaxed text-foreground/90">
            {brief.product_concept.content}
          </motion.p>
          <SlideFooter prompt={brief.product_concept.prompt} section="product_concept" onReroll={handleReroll} rerolling={rerolling} />
        </Slide>

        <Slide index={2} kicker="02 — Target User" title="Who It's For" testid="slide-target-user">
          <motion.p variants={item} data-testid="target-user-content" className="text-2xl sm:text-3xl leading-snug font-display font-light text-foreground">
            {brief.target_user.content}
          </motion.p>
          <SlideFooter prompt={brief.target_user.prompt} section="target_user" onReroll={handleReroll} rerolling={rerolling} />
        </Slide>

        <Slide index={3} kicker="03 — Core Value Proposition" title="Why It Wins" testid="slide-value-proposition">
          <motion.p variants={item} data-testid="value-proposition-content" className="text-2xl sm:text-3xl leading-snug font-display font-light text-emerald-400">
            {brief.value_proposition.content}
          </motion.p>
          <SlideFooter prompt={brief.value_proposition.prompt} section="value_proposition" onReroll={handleReroll} rerolling={rerolling} />
        </Slide>

        <Slide index={4} kicker="04 — 3 Key Features" title="What You Build" testid="slide-key-features" isLast>
          <motion.div variants={item} data-testid="key-features-content" className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {brief.key_features.features.map((f, i) => (
              <div
                key={f}
                className="rounded-xl border border-border bg-card p-6 flex flex-col gap-4 hover:border-emerald-500/50 transition-colors duration-200"
              >
                <span className="font-mono text-sm text-emerald-500">0{i + 1}</span>
                <p className="text-base leading-relaxed text-foreground/90">{f}</p>
              </div>
            ))}
          </motion.div>
          <SlideFooter prompt={brief.key_features.prompt} section="key_features" onReroll={handleReroll} rerolling={rerolling} />
        </Slide>
      </div>
    );
  }

  // ---------- LOADING STATE ----------
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center px-8 relative z-10">
        <Toaster theme="dark" position="top-center" />
        <div className="max-w-lg w-full">
          <div className="flex items-center gap-3 mb-8">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3, ease: "linear" }}>
              <Wand2 className="h-6 w-6 text-emerald-500" />
            </motion.div>
            <span className="font-display text-2xl font-medium">Fusing…</span>
          </div>
          <LoadingSequence />
        </div>
      </div>
    );
  }

  // ---------- INPUT STATE ----------
  return (
    <div className="min-h-screen w-full flex flex-col justify-center px-8 sm:px-16 md:px-24 relative z-10 py-20">
      <Toaster theme="dark" position="top-center" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-2xl w-full mx-auto"
      >
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Wand2 className="h-4 w-4 text-white" />
          </div>
          <span className="font-display text-lg font-medium tracking-tight">BrainIdeasFused</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-6">
          Fuse scattered pains<br />into one product.
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-10 max-w-xl">
          Paste 3–5 Reddit titles, tweets, or complaints — or drop a subreddit / thread URL
          and we'll pull the top posts. BrainIdeasFused extracts the common pain points and
          synthesizes a single, sellable product opportunity brief.
        </p>

        <div className="mb-6">
          <label className="font-mono text-xs uppercase tracking-widest text-emerald-500 mb-3 block">
            Pull from Reddit
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Link2 className="h-4 w-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="reddit-url-input"
                value={redditUrl}
                onChange={(e) => setRedditUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleReddit()}
                placeholder="reddit.com/r/SaaS  or a thread URL"
                className="w-full rounded-xl border border-border bg-card/60 backdrop-blur pl-11 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:border-emerald-500 focus-visible:outline-none transition-colors duration-200 font-mono"
              />
            </div>
            <button
              data-testid="reddit-fetch-button"
              onClick={handleReddit}
              disabled={fetchingReddit}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-5 py-3 text-sm font-medium transition-colors duration-200 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none whitespace-nowrap"
            >
              {fetchingReddit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {fetchingReddit ? "Pulling…" : "Pull posts"}
            </button>
          </div>
          <div className="flex items-center gap-4 my-6">
            <span className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or paste manually</span>
            <span className="h-px flex-1 bg-border" />
          </div>
        </div>

        <textarea
          data-testid="snippets-textarea"
          value={snippets}
          onChange={(e) => setSnippets(e.target.value)}
          placeholder={"Paste 3–5 snippets, one per line…\n\n" + EXAMPLE}
          className="w-full min-h-[220px] rounded-xl border border-border bg-card/60 backdrop-blur p-5 text-base leading-relaxed text-foreground placeholder:text-muted-foreground/50 resize-y focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:border-emerald-500 focus-visible:outline-none transition-colors duration-200 font-mono text-sm"
        />

        <div className="flex flex-wrap items-center gap-4 mt-6">
          <button
            data-testid="fuse-button"
            onClick={handleFuse}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-7 py-3.5 text-base font-medium shadow-lg shadow-emerald-500/20 transition-all duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
          >
            <Wand2 className="h-4.5 w-4.5" /> Fuse
          </button>
          <button
            data-testid="load-example-button"
            onClick={() => setSnippets(EXAMPLE)}
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Try an example
          </button>
        </div>
      </motion.div>
    </div>
  );
}
